import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { mainSiteUrl } from '@/mocks/categories';

export default function Footer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleNewsletter = async (e: React.FormEvent) => {
    e.preventDefault();

    const form = e.currentTarget as HTMLFormElement;
    const honeypot = form.querySelector('input[name="website_alt"]') as HTMLInputElement;
    if (honeypot?.value.trim()) {
      setStatus('success');
      setEmail('');
      setTimeout(() => setStatus('idle'), 3000);
      return;
    }

    if (!email.trim()) return;
    setStatus('loading');

    try {
      await fetch('https://readdy.ai/api/form/d8vqbki181e9e92u8ue0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email }).toString(),
      });
      setStatus('success');
      setEmail('');
      setTimeout(() => setStatus('idle'), 3000);
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <footer className="bg-secondary-800 text-background-50 py-16 md:py-20 px-4 md:px-6">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-12 mb-16">
          <div>
            <a href="/" className="text-xl font-heading font-bold text-background-50 whitespace-nowrap">
              cssbuy<span className="text-accent-500">.pro</span>
            </a>
            <p className="text-xs text-foreground-300 mt-3 leading-relaxed max-w-xs">
              {t('footer_description')}
            </p>
          </div>

          <div>
            <p className="text-xs text-foreground-300 uppercase tracking-widest mb-4 font-semibold">
              {t('footer_newsletter')}
            </p>
            <form id="newsletter-form" onSubmit={handleNewsletter} data-readdy-form="">
              <div className="relative">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('footer_newsletter_placeholder')}
                  required
                  className="w-full bg-transparent border-b border-foreground-400/40 pb-2 text-sm text-background-50 placeholder:text-foreground-400 outline-none focus:border-accent-500 transition-colors"
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="absolute right-0 top-0 w-6 h-6 flex items-center justify-center text-foreground-300 hover:text-accent-500 transition-colors cursor-pointer"
                >
                  <i className={`${status === 'loading' ? 'ri-loader-4-line animate-spin' : 'ri-arrow-right-line'} text-sm`}></i>
                </button>
              </div>
              <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              <p className="text-[10px] text-foreground-400 mt-2">
                {t('footer_newsletter_hint')}
              </p>
              {status === 'success' && (
                <p className="text-xs text-accent-500 mt-2">Inscrito com sucesso!</p>
              )}
              {status === 'error' && (
                <p className="text-xs text-red-400 mt-2">Erro. Tente novamente.</p>
              )}
            </form>
          </div>

          <div>
            <p className="text-xs text-foreground-300 uppercase tracking-widest mb-4 font-semibold">
              {t('footer_links_title')}
            </p>
            <div className="flex flex-col gap-3">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="text-sm text-background-50/80 hover:text-accent-500 transition-colors whitespace-nowrap"
              >
                {t('footer_link_spreadsheet')}
              </a>
              <button
                onClick={() => navigate('/artigo/guia-completo-cssbuy-spreadsheet-2026')}
                className="text-sm text-background-50/80 hover:text-accent-500 transition-colors text-left whitespace-nowrap cursor-pointer"
              >
                {t('footer_link_shipping')}
              </button>
              <a href="/#faq" className="text-sm text-background-50/80 hover:text-accent-500 transition-colors whitespace-nowrap">
                {t('footer_link_faq')}
              </a>
            </div>
          </div>

          <div>
            <p className="text-xs text-foreground-300 uppercase tracking-widest mb-4 font-semibold">
              {t('footer_social_title')}
            </p>
            <div className="flex flex-col gap-3">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="text-sm text-background-50/80 hover:text-accent-500 transition-colors whitespace-nowrap"
              >
                <span className="w-4 h-4 inline-flex items-center justify-center mr-2">
                  <i className="ri-global-line"></i>
                </span>
                Website
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="text-sm text-background-50/80 hover:text-accent-500 transition-colors whitespace-nowrap"
              >
                <span className="w-4 h-4 inline-flex items-center justify-center mr-2">
                  <i className="ri-links-line"></i>
                </span>
                CSSBuy Links
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="text-sm text-background-50/80 hover:text-accent-500 transition-colors whitespace-nowrap"
              >
                <span className="w-4 h-4 inline-flex items-center justify-center mr-2">
                  <i className="ri-shield-check-line"></i>
                </span>
                Segurança
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-foreground-400/20 pt-8">
          <div className="text-4xl md:text-6xl lg:text-7xl font-heading font-black text-background-50/10 text-center select-none tracking-tight">
            CSSBUY
          </div>
          <p className="text-center text-xs text-foreground-400 mt-4">
            {t('footer_rights')}
          </p>
        </div>
      </div>
    </footer>
  );
}